import { Stack, Switch, Typography } from '@mui/material';
import { graphql } from '../../../../../gql';
import { FC } from 'react';
import { useIntl } from 'react-intl';
import { DrawerPersona } from './PersonasEditDrawer';
import { useApiMutation } from '../../../../../query/hooks';
import { PersonaAvatar } from '../../../../../persona/PersonaAvatar';
import slugify from 'slugify';
import { mutationResponseHandler } from '../../../../../utils/mutationResponseHandler';
import { useSnackbar } from '../../../../../store/snackbar';

const connectPersonaToWidget = graphql(`
	mutation personasWidget_connectPersonaToWidget($activityId: UUID!, $personaId: UUID!) {
		updateActivity(by: { id: $activityId }, data: { personas: { connect: { id: $personaId } } }) {
			ok
			errorMessage
		}
	}
`);

const disconnectPersonaFromWidget = graphql(`
	mutation personasWidget_disconnectPersonaFromWidget($activityId: UUID!, $personaId: UUID!) {
		updateActivity(by: { id: $activityId }, data: { personas: { disconnect: { id: $personaId } } }) {
			ok
			errorMessage
		}
	}
`);

type Props = Readonly<DrawerPersona & { isAddedToActivity: boolean; activityId: string }>;

export const PersonaDrawerRow: FC<Props> = ({ isAddedToActivity, activityId, ...rest }) => {
	const intl = useIntl();
	const { mutate: connectPersonaMutation } = useApiMutation(connectPersonaToWidget);
	const { mutate: disconnectPersonaMutation } = useApiMutation(disconnectPersonaFromWidget);
	const { openSnackbar } = useSnackbar();
	const connectPersona = (personaId: string, name: string) => {
		connectPersonaMutation(
			{ personaId, activityId },
			{
				onSuccess: (result) => {
					mutationResponseHandler({
						result: result.updateActivity.ok,
						toastMessages: {
							success: intl.formatMessage({ id: 'widget.persona.success.connected' }, { name }),
							error: intl.formatMessage({ id: 'widget.persona.error.connected' }),
						},
						errorMessage: result.updateActivity.errorMessage,
						openSnackbar,
					});
				},
			},
		);
	};

	const dicconnectPersona = (personaId: string, name: string) => {
		disconnectPersonaMutation(
			{ personaId, activityId },
			{
				onSuccess: (result) => {
					mutationResponseHandler({
						result: result.updateActivity.ok,
						toastMessages: {
							success: intl.formatMessage({ id: 'widget.persona.success.disconnected' }, { name }),
							error: intl.formatMessage({ id: 'widget.persona.error.disconnected' }),
						},
						errorMessage: result.updateActivity.errorMessage,
						openSnackbar,
					});
				},
			},
		);
	};

	return (
		<Stack
			data-persona={slugify(rest.name, { lower: true })}
			display={'flex'}
			columnGap={2}
			direction={'row'}
			justifyContent={'flex-start'}
			alignItems={'center'}
			flexWrap={'nowrap'}
		>
			<PersonaAvatar persona={rest} />
			<Stack>
				<Typography fontWeight={500}>{rest.name}</Typography>
				{/* TODO: Add role */}
			</Stack>
			<Switch
				size={'medium'}
				sx={{ marginLeft: 'auto' }}
				checked={isAddedToActivity}
				onChange={() => (isAddedToActivity ? dicconnectPersona(rest.id, rest.name) : connectPersona(rest.id, rest.name))}
			/>
		</Stack>
	);
};
